import { createSelector } from "@reduxjs/toolkit";
import {
  openapi,
  type GetMcbimFileFloorVersionsAllApiResponse as IGetMcbimFileFloorVersionsAllApiResponse,
  type PostMcbimItemsApiArg as IPostMcbimItemsApiArg,
  type PostMcbimItemsApiResponse as IPostMcbimItemsApiResponse,
} from "../../openapi";
import type { IPixPolygon } from "@multiconsult-group/multiplacer-ui";
import type { IRootState } from "../app/store";
import { filterAndTransformMcBiItemsToPixPolygons } from "../DesignTab/transformMCBimItemsToPolygons";
import { selectProjectNo } from "../DesignTab/designSlice";
import {
  selectProjectViewerSelectedFileFloorKeys,
  selectProjectViewerZClipRangeM,
} from "./projectViewerSlice";

type IProjectViewerFileFloorRow = IGetMcbimFileFloorVersionsAllApiResponse[number];
type IProjectViewerMcbimItem = IPostMcbimItemsApiResponse[number];

export type IProjectViewerFloorOption = {
  building: string;
  name: string;
  globalId: string;
  selectionKey: string;
  rowCount: number;
};

export type IProjectViewerFileOption = {
  fileName: string;
  floors: IProjectViewerFloorOption[];
  rowCount: number;
  selectedFloorCount: number;
  checked: boolean;
  indeterminate: boolean;
};

export type IProjectViewerMcbimItemsQueryArg = IPostMcbimItemsApiArg;

export type IProjectViewerZBoundsM = {
  minM: number | null;
  maxM: number | null;
};

export type IProjectViewerTransformPivot = {
  x: number;
  y: number;
} | null;

const EMPTY_FILE_FLOOR_ROWS: IProjectViewerFileFloorRow[] = [];
const EMPTY_MCBIM_ITEMS_QUERY_ARGS: IProjectViewerMcbimItemsQueryArg[] = [];
const EMPTY_MCBIM_ITEMS: IProjectViewerMcbimItem[] = [];
const EMPTY_PIX_POLYGONS: IPixPolygon[] = [];
const EMPTY_FILE_NAMES: string[] = [];
const EMPTY_Z_BOUNDS_M: IProjectViewerZBoundsM = {
  minM: null,
  maxM: null,
};

export const getProjectViewerFileFloorKey = (fileName: string, buildingStoryGlobalId: string) =>
  `${fileName}::${buildingStoryGlobalId}`;

export const selectProjectViewerFileFloorVersionsAll = (
  state: IRootState
): IProjectViewerFileFloorRow[] => {
  const projectNo = selectProjectNo(state);
  if (!projectNo) return EMPTY_FILE_FLOOR_ROWS;

  return (
    openapi.endpoints.getMcbimFileFloorVersionsAll.select({ projectNo })(state).data ??
    EMPTY_FILE_FLOOR_ROWS
  );
};

export const selectProjectViewerSelectedFileFloorKeySet = createSelector(
  [selectProjectViewerSelectedFileFloorKeys],
  (selectedFileFloorKeys): Set<string> => new Set(selectedFileFloorKeys)
);

export const selectProjectViewerFloorOptions = createSelector(
  [selectProjectViewerFileFloorVersionsAll],
  (fileFloorVersions): IProjectViewerFloorOption[] => {
    const byGlobalId = new Map<string, IProjectViewerFloorOption>();

    for (const row of fileFloorVersions) {
      const existing = byGlobalId.get(row.buildingStoryGlobalId);
      if (existing) {
        existing.rowCount += 1;
        continue;
      }

      byGlobalId.set(row.buildingStoryGlobalId, {
        building: row.building,
        name: row.buildingStoryName,
        globalId: row.buildingStoryGlobalId,
        selectionKey: getProjectViewerFileFloorKey(row.fileName, row.buildingStoryGlobalId),
        rowCount: 1,
      });
    }

    return Array.from(byGlobalId.values()).sort((a, b) => {
      const buildingSort = a.building.localeCompare(b.building);
      if (buildingSort !== 0) return buildingSort;
      return a.name.localeCompare(b.name);
    });
  }
);

export const selectProjectViewerAllFileFloorKeys = createSelector(
  [selectProjectViewerFileFloorVersionsAll],
  (fileFloorVersions): string[] =>
    Array.from(
      new Set(
        fileFloorVersions.map((row) =>
          getProjectViewerFileFloorKey(row.fileName, row.buildingStoryGlobalId)
        )
      )
    )
);

export const selectProjectViewerFileOptions = createSelector(
  [selectProjectViewerFileFloorVersionsAll, selectProjectViewerSelectedFileFloorKeySet],
  (fileFloorVersions, selectedFileFloorKeySet): IProjectViewerFileOption[] => {
    const byFileName = new Map<
      string,
      {
        fileName: string;
        floorsByGlobalId: Map<string, IProjectViewerFloorOption>;
        rowCount: number;
      }
    >();

    for (const row of fileFloorVersions) {
      const fileGroup = byFileName.get(row.fileName) ?? {
        fileName: row.fileName,
        floorsByGlobalId: new Map<string, IProjectViewerFloorOption>(),
        rowCount: 0,
      };

      fileGroup.rowCount += 1;

      const existingFloor = fileGroup.floorsByGlobalId.get(row.buildingStoryGlobalId);
      if (existingFloor) {
        existingFloor.rowCount += 1;
      } else {
        fileGroup.floorsByGlobalId.set(row.buildingStoryGlobalId, {
          building: row.building,
          name: row.buildingStoryName,
          globalId: row.buildingStoryGlobalId,
          selectionKey: getProjectViewerFileFloorKey(row.fileName, row.buildingStoryGlobalId),
          rowCount: 1,
        });
      }

      byFileName.set(row.fileName, fileGroup);
    }

    return Array.from(byFileName.values())
      .map((fileGroup) => {
        const floors = Array.from(fileGroup.floorsByGlobalId.values()).sort((a, b) => {
          const buildingSort = a.building.localeCompare(b.building);
          if (buildingSort !== 0) return buildingSort;
          return a.name.localeCompare(b.name);
        });
        const selectedFloorCount = floors.filter((floor) =>
          selectedFileFloorKeySet.has(floor.selectionKey)
        ).length;

        return {
          fileName: fileGroup.fileName,
          rowCount: fileGroup.rowCount,
          floors,
          selectedFloorCount,
          checked: selectedFloorCount > 0 && selectedFloorCount === floors.length,
          indeterminate: selectedFloorCount > 0 && selectedFloorCount < floors.length,
        };
      })
      .sort((a, b) => a.fileName.localeCompare(b.fileName));
  }
);

export const selectProjectViewerFileFloorVersionCount = createSelector(
  [selectProjectViewerFileFloorVersionsAll],
  (fileFloorVersions): number => fileFloorVersions.length
);

export const selectProjectViewerFileFloorZBoundsM = createSelector(
  [selectProjectViewerFileFloorVersionsAll, selectProjectViewerSelectedFileFloorKeySet],
  (fileFloorVersions, selectedFileFloorKeySet): IProjectViewerZBoundsM => {
    const rows =
      selectedFileFloorKeySet.size === 0
        ? fileFloorVersions
        : fileFloorVersions.filter((row) =>
            selectedFileFloorKeySet.has(
              getProjectViewerFileFloorKey(row.fileName, row.buildingStoryGlobalId)
            )
          );

    let minM: number | null = null;
    let maxM: number | null = null;

    for (const row of rows) {
      const bottom = row.buildingStoryElevation;
      const top = row.nextBuildingStoryElevation;

      if (typeof bottom === "number") {
        minM = minM == null ? bottom : Math.min(minM, bottom);
        maxM = maxM == null ? bottom : Math.max(maxM, bottom);
      }

      if (typeof top === "number") {
        minM = minM == null ? top : Math.min(minM, top);
        maxM = maxM == null ? top : Math.max(maxM, top);
      }
    }

    if (minM == null || maxM == null) return EMPTY_Z_BOUNDS_M;

    return { minM, maxM };
  }
);

export const selectProjectViewerMcbimItemsQueryArgs = createSelector(
  [
    selectProjectNo,
    selectProjectViewerFileFloorVersionsAll,
    selectProjectViewerSelectedFileFloorKeySet,
  ],
  (
    projectNo,
    fileFloorVersions,
    selectedFileFloorKeySet
  ): IProjectViewerMcbimItemsQueryArg[] => {
    if (!projectNo || selectedFileFloorKeySet.size === 0) {
      return EMPTY_MCBIM_ITEMS_QUERY_ARGS;
    }

    const instanceClassesByFileFloorKey = new Map<
      string,
      {
        buildingStoryGlobalId: string;
        fileName: string;
        instanceClasses: Set<string>;
      }
    >();

    for (const row of fileFloorVersions) {
      const selectionKey = getProjectViewerFileFloorKey(row.fileName, row.buildingStoryGlobalId);
      if (!selectedFileFloorKeySet.has(selectionKey)) continue;

      const fileFloorGroup = instanceClassesByFileFloorKey.get(selectionKey) ?? {
        buildingStoryGlobalId: row.buildingStoryGlobalId,
        fileName: row.fileName,
        instanceClasses: new Set<string>(),
      };

      for (const instanceClass of row.instanceClasses) {
        if (instanceClass) fileFloorGroup.instanceClasses.add(instanceClass);
      }

      instanceClassesByFileFloorKey.set(selectionKey, fileFloorGroup);
    }

    return Array.from(instanceClassesByFileFloorKey.values())
      .map((fileFloorGroup) => ({
        body: {
          projectNo,
          buildingStoryGlobalId: fileFloorGroup.buildingStoryGlobalId,
          fileName: fileFloorGroup.fileName,
          instanceClasses: Array.from(fileFloorGroup.instanceClasses).sort((a, b) =>
            a.localeCompare(b)
          ),
        },
      }))
      .filter((queryArg) => queryArg.body.instanceClasses.length > 0);
  }
);

export const selectProjectViewerVisibleMcbimFileNames = createSelector(
  [selectProjectViewerFileFloorVersionsAll, selectProjectViewerSelectedFileFloorKeySet],
  (fileFloorVersions, selectedFileFloorKeySet): string[] => {
    if (selectedFileFloorKeySet.size === 0) return EMPTY_FILE_NAMES;

    const fileNames = new Set<string>();

    for (const row of fileFloorVersions) {
      const selectionKey = getProjectViewerFileFloorKey(row.fileName, row.buildingStoryGlobalId);
      if (selectedFileFloorKeySet.has(selectionKey)) {
        fileNames.add(row.fileName);
      }
    }

    return Array.from(fileNames).sort((a, b) => a.localeCompare(b));
  }
);

export const selectProjectViewerMcbimItems = createSelector(
  [selectProjectViewerMcbimItemsQueryArgs, (state: IRootState) => state],
  (queryArgs, state): IProjectViewerMcbimItem[] => {
    if (!queryArgs.length) return EMPTY_MCBIM_ITEMS;

    return queryArgs.flatMap(
      (queryArg) => openapi.endpoints.postMcbimItems.select(queryArg)(state).data ?? []
    );
  }
);

export const selectProjectViewerZBoundsM = createSelector(
  [selectProjectViewerMcbimItems, selectProjectViewerFileFloorZBoundsM],
  (mcbimItems, fileFloorZBoundsM): IProjectViewerZBoundsM => {
    let minM: number | null = null;
    let maxM: number | null = null;

    for (const item of mcbimItems) {
      const z = item.coordinate?.[2];
      if (typeof z !== "number") continue;

      minM = minM == null ? z : Math.min(minM, z);
      maxM = maxM == null ? z : Math.max(maxM, z);
    }

    if (minM == null || maxM == null) return fileFloorZBoundsM;

    return { minM, maxM };
  }
);

export const selectProjectViewerTransformPivot = createSelector(
  [selectProjectViewerMcbimItems],
  (mcbimItems): IProjectViewerTransformPivot => {
    let minX: number | null = null;
    let maxX: number | null = null;
    let minY: number | null = null;
    let maxY: number | null = null;

    for (const item of mcbimItems) {
      const points = item.bbox ?? item.polygon;
      if (!points?.length) continue;

      for (const point of points) {
        const [x, y] = point;
        if (typeof x !== "number" || typeof y !== "number") continue;

        minX = minX == null ? x : Math.min(minX, x);
        maxX = maxX == null ? x : Math.max(maxX, x);
        minY = minY == null ? y : Math.min(minY, y);
        maxY = maxY == null ? y : Math.max(maxY, y);
      }
    }

    if (minX == null || maxX == null || minY == null || maxY == null) return null;

    return {
      x: (minY + maxY) / 2,
      y: (minX + maxX) / 2,
    };
  }
);

export const selectProjectViewerPixPolygons = createSelector(
  [selectProjectViewerMcbimItems, selectProjectViewerZClipRangeM],
  (mcbimItems, zClipRangeM): IPixPolygon[] => {
    if (!mcbimItems.length) return EMPTY_PIX_POLYGONS;

    const { bottomM, topM } = zClipRangeM;
    const filteredMcbimItems =
      bottomM == null || topM == null || topM <= bottomM
        ? mcbimItems
        : mcbimItems.filter((item) => {
            const z = item.coordinate?.[2];
            return typeof z === "number" && z >= bottomM && z <= topM;
          });

    if (!filteredMcbimItems.length) return EMPTY_PIX_POLYGONS;

    return filterAndTransformMcBiItemsToPixPolygons(
      filteredMcbimItems,
      "MULTIPLACER_COLORS",
      undefined,
      null,
      null
    );
  }
);

export const selectProjectViewerZoomExtentKey = createSelector(
  [selectProjectViewerMcbimItems],
  (mcbimItems): string | null => {
    if (!mcbimItems.length) return null;

    const firstItem = mcbimItems[0];
    const lastItem = mcbimItems[mcbimItems.length - 1];

    return `${mcbimItems.length}:${firstItem?._id ?? ""}:${lastItem?._id ?? ""}`;
  }
);
